import { useState } from "react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";

import { type B2bUsersNodeKind } from "@/presentation/data/b2b-users-stage-seed";
import { DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";

export interface AddComponentFormValues {
  label: string;
  role: string;
  domain: Domain;
  kind: B2bUsersNodeKind;
}

interface AddComponentDialogProps {
  open: boolean;
  defaultKind?: B2bUsersNodeKind;
  defaultDomain?: Domain;
  onSubmit: (values: AddComponentFormValues) => void;
  onClose: () => void;
}

export function AddComponentDialog({
  open,
  defaultKind = "system",
  defaultDomain = "app-core",
  onSubmit,
  onClose,
}: AddComponentDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        {open ? (
          <AddComponentBody
            key={`${defaultKind}-${defaultDomain}`}
            defaultKind={defaultKind}
            defaultDomain={defaultDomain}
            onSubmit={onSubmit}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface BodyProps {
  defaultKind: B2bUsersNodeKind;
  defaultDomain: Domain;
  onSubmit: (values: AddComponentFormValues) => void;
  onClose: () => void;
}

function AddComponentBody({ defaultKind, defaultDomain, onSubmit, onClose }: BodyProps) {
  const [label, setLabel] = useState("");
  const [role, setRole] = useState("");
  const [domain, setDomain] = useState<Domain>(defaultDomain);
  const [kind, setKind] = useState<B2bUsersNodeKind>(defaultKind);

  const trimmed = label.trim();
  const canSubmit = trimmed.length > 0;

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({ label: trimmed, role: role.trim(), domain, kind });
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add {kind === "actor" ? "actor" : "component"}</DialogTitle>
        <DialogDescription>
          Drops a new node onto the live B2B users journey map at the center of the viewport.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-2">
        <div className="grid gap-1.5">
          <Label htmlFor="ac-label">Label</Label>
          <Input
            id="ac-label"
            autoFocus
            value={label}
            placeholder={kind === "actor" ? "Buyer / AM / Driver…" : "B2B Pricing Engine"}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) handleSubmit();
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ac-role">Role (optional)</Label>
          <Input
            id="ac-role"
            value={role}
            placeholder="One-liner — what does this do?"
            onChange={(e) => setRole(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as B2bUsersNodeKind)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="actor">Actor (person)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Domain</Label>
            <Select value={domain} onValueChange={(v) => setDomain(v as Domain)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOMAINS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {DOMAIN_LABEL[d]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
          Add to map
        </Button>
      </DialogFooter>
    </>
  );
}
